import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { layers } from '@atlaskit/theme/constants';

import { AISmartButton } from '@atlassian/ai-smart-button';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';

import { expVal } from '@confluence/feature-experiments';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { useConversationAssistantConfig } from '@confluence/smart-create/entry-points/useConfig.ts';
import { isPersonalSpace } from '@confluence/space-utils/entry-points/isPersonalSpace.ts';
import { useIsNewUser } from '@confluence/targeting/entry-points/useIsNewUser.ts';
/* eslint-disable import/no-unresolved -- Confluence entrypoint resolver false positive. */
import {
	FIRST_TWO_WEEKS_THRESHOLD,
	getWelcomeFromRovoChatEligibility,
} from '@confluence/experiment-welcome-from-rovo/entry-points/welcomeFromRovoChatEligibility.ts';
import { useWelcomeFromRovoChatUiConfig } from '@confluence/experiment-welcome-from-rovo/entry-points/welcomeFromRovoChatUiConfig.ts';
import { WELCOME_FROM_ROVO_CHAT } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	onboardingStateGetOnboardingStateQuery,
	type onboardingStateGetOnboardingStateQuery$data,
	type onboardingStateGetOnboardingStateQuery$variables,
} from '@confluence/onboarding-helpers/entry-points/onboardingStateGetOnboardingStateQuery.ts';
import {
	deserializeState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
/* eslint-enable import/no-unresolved */
import {
	useRovoConfigGetActions,
	useRovoConfigTags,
	useRovoConfigCustomGreeting,
	useRovoConfigShouldShowGreeting,
	useRovoConfigPrioritizeCustomActionResults,
	useRovoConfigNumStartersToShow,
} from '@confluence/rovo-config-provider-common/entry-points/useRovoCurrentConfig.ts';

import { useCreateRovoExperienceSidebarController } from './hooks/useCreateRovoExperienceSidebarController.ts';

const styles = cssMap({
	smartButtonWrapperStyles: {
		position: 'absolute',
		zIndex: layers.modal(),
		right: 0,
		bottom: 0,
	},
});

type AISmartButtonProps = {
	contentId: string;
	contentType: string;
	pageMode: string;
	isDisabled?: boolean;
	spaceKey?: string;
	/**
	 * Optional override for sidebar availability. When false, the "switch to sidebar"
	 * option is hidden. Used in embedded Confluence where sidebar layout infrastructure
	 * is not available.
	 */
	isSidebarAvailable?: boolean;
};

export function AISmartButtonWrapper({
	contentId,
	contentType,
	pageMode,
	isDisabled = false,
	spaceKey,
	isSidebarAvailable,
}: AISmartButtonProps): React.JSX.Element | null {
	const isLiveDoc = pageMode === 'live';
	const [{ isViewMode: isLiveViewMode }] = useLivePageMode();
	// Only pass isLiveViewMode when on a live doc — avoids threading irrelevant state to downstream components
	const liveViewMode = isLiveDoc ? isLiveViewMode : undefined;
	const { cloudId, orgId } = useSessionData();
	const { isAIEnabledForContent, isRovoEnabledForContent } = useIsAIEnabledForContent({
		contentId,
	});
	const sidebarApi = useCreateRovoExperienceSidebarController();
	const getActions = useRovoConfigGetActions();
	const tags = useRovoConfigTags();
	const customGreeting = useRovoConfigCustomGreeting();
	const shouldShowGreeting = useRovoConfigShouldShowGreeting();
	const prioritizeCustomActionResults = useRovoConfigPrioritizeCustomActionResults();
	const numStartersToShow = useRovoConfigNumStartersToShow();
	const { mode, isMiniModalAvailable } = useRovoExperienceAPIStore();
	const publishRovo = usePublish('ai-mate');
	const hasAutoOpenedWelcomeFromRovoChatRef = useRef(false);
	const hasShownWelcomeFromRovoChatUiRef = useRef(false);
	const [shouldUseWelcomeFromRovoChatUi, setShouldUseWelcomeFromRovoChatUi] =
		useState(false);
	const welcomeFromRovoChatUiConfig = useWelcomeFromRovoChatUiConfig();

	// environment-aware enablement via feature encoding
	const isAIEnvEnabled = isFeatureEnabled('confluence', 'confluence-ai');
	const isEnabled = isAIEnvEnabled && isAIEnabledForContent && isRovoEnabledForContent;

	const shouldSkipWelcomeFromRovoChatEligibilityCheck =
		!isEnabled ||
		!spaceKey ||
		isPersonalSpace(spaceKey) ||
		!expVal('cc_ai_growth_welcome_from_rovo_eligible', 'isEnabled', false);
	const { isNewUser, loading: isNewUserLoading } = useIsNewUser(
		{ activeDaysThreshold: FIRST_TWO_WEEKS_THRESHOLD },
		shouldSkipWelcomeFromRovoChatEligibilityCheck,
	);
	const welcomeFromRovoChatEligibility = getWelcomeFromRovoChatEligibility({
		isNewUser,
		isNewUserLoading,
		shouldSkipEligibilityCheck: shouldSkipWelcomeFromRovoChatEligibilityCheck,
	});
	const {
		data: welcomeFromRovoChatOnboardingStateData,
		loading: welcomeFromRovoChatOnboardingStateLoading,
		error: welcomeFromRovoChatOnboardingStateError,
	} = useQueryInterop<
		onboardingStateGetOnboardingStateQuery$data,
		onboardingStateGetOnboardingStateQuery$variables
	>(onboardingStateGetOnboardingStateQuery, {
		variables: { key: [WELCOME_FROM_ROVO_CHAT.CHAT_AUTO_OPENED] },
		skip: !welcomeFromRovoChatEligibility.isEligible,
	});
	const { setOnboardingState } = useSetOnboardingState();
	const { chatAutoOpened: hasPreviouslyAutoOpenedWelcomeFromRovoChat } = deserializeState(
		welcomeFromRovoChatOnboardingStateData,
	) as {
		chatAutoOpened?: boolean;
	};
	useEffect(() => {
		if (
			!welcomeFromRovoChatEligibility.isEligible ||
			welcomeFromRovoChatOnboardingStateLoading ||
			welcomeFromRovoChatOnboardingStateError ||
			hasPreviouslyAutoOpenedWelcomeFromRovoChat ||
			hasAutoOpenedWelcomeFromRovoChatRef.current ||
			!isMiniModalAvailable ||
			isDisabled ||
			// Keep this call inline so exposure only happens after the local eligibility checks pass.
			!expVal('cc_ai_growth_welcome_from_rovo', 'isEnabled', false)
		) {
			return;
		}

		if (mode !== undefined) {
			hasAutoOpenedWelcomeFromRovoChatRef.current = true;
			setOnboardingState({
				key: WELCOME_FROM_ROVO_CHAT.CHAT_AUTO_OPENED,
				value: 'true',
			});
			return;
		}

		const frameId = requestAnimationFrame(() => {
			if (hasAutoOpenedWelcomeFromRovoChatRef.current) {
				return;
			}

			hasAutoOpenedWelcomeFromRovoChatRef.current = true;
			setShouldUseWelcomeFromRovoChatUi(true);
			publishRovo({
				type: 'chat-new',
				source: 'welcome-from-rovo-chat',
				openChat: true,
				openChatMode: 'mini-modal',
				data: { dialogues: [] },
			});
			setOnboardingState({
				key: WELCOME_FROM_ROVO_CHAT.CHAT_AUTO_OPENED,
				value: 'true',
			});
		});

		return () => cancelAnimationFrame(frameId);
	}, [
		hasPreviouslyAutoOpenedWelcomeFromRovoChat,
		isDisabled,
		isMiniModalAvailable,
		mode,
		publishRovo,
		setOnboardingState,
		welcomeFromRovoChatEligibility.isEligible,
		welcomeFromRovoChatOnboardingStateError,
		welcomeFromRovoChatOnboardingStateLoading,
	]);

	useEffect(() => {
		if (mode === 'mini-modal' && shouldUseWelcomeFromRovoChatUi) {
			hasShownWelcomeFromRovoChatUiRef.current = true;
		}
	}, [mode, shouldUseWelcomeFromRovoChatUi]);

	useEffect(() => {
		if (mode !== 'mini-modal' && hasShownWelcomeFromRovoChatUiRef.current) {
			hasShownWelcomeFromRovoChatUiRef.current = false;
			setShouldUseWelcomeFromRovoChatUi(false);
		}
	}, [mode]);

	// 48px = nav header height, 8px = object sidebar bottom padding, 16px = AI smart button bottom margin, 8px = extra margin so it doesn't align with nav header
	const navbarOffset = 48 + 8 + 16 + 8;
	// From confluence/next/packages/content-types-header/src/ContentTypesHeader.tsx
	const OBJECT_HEADER_CONTAINER_HEIGHT = 56;
	// From platform/packages/editor/editor-shared-styles/src/consts/consts.ts
	const FULL_PAGE_EDITOR_TOOLBAR_HEIGHT = 40;

	// Calculate total vertical offset based on the maximum header height (occurs in edit mode)
	const totalOffset =
		FULL_PAGE_EDITOR_TOOLBAR_HEIGHT + OBJECT_HEADER_CONTAINER_HEIGHT + navbarOffset;

	// Memoize analytics attributes to prevent unnecessary re-renders
	const analyticsAttributes = useMemo(
		() => ({
			pageMode,
		}),
		[pageMode],
	);

	const baseUiConfig = useMemo(
		() => ({
			...(customGreeting ? { customGreeting } : {}),
			...(shouldShowGreeting !== undefined
				? {
						elements: { shouldShowGreeting },
					}
				: {}),
		}),
		[customGreeting, shouldShowGreeting],
	);

	const shouldShowWelcomeFromRovoChatUi =
		shouldUseWelcomeFromRovoChatUi && mode === 'mini-modal';

	const uiConfig = useMemo(() => {
		if (!shouldShowWelcomeFromRovoChatUi) {
			return baseUiConfig;
		}

		return {
			...baseUiConfig,
			customGreeting: welcomeFromRovoChatUiConfig.customGreeting,
			elements: {
				...baseUiConfig.elements,
				shouldShowGreeting: true,
				shouldShowConversationActions: false,
			},
		};
	}, [baseUiConfig, shouldShowWelcomeFromRovoChatUi, welcomeFromRovoChatUiConfig]);

	const overrideMiniModalWrapper = useMemo(
		() => (children: React.ReactNode) => {
			return <Box xcss={styles.smartButtonWrapperStyles}>{children}</Box>;
		},
		[],
	);
	const conversationAssistantConfig = useConversationAssistantConfig({
		assistanceServiceConfigHeaders: {
			'x-issynthetic-traffic': 'false',
		},
		preventAbortRequestsOnStagingModalClose: true,
		preventAbortRequestsOnStandaloneChatClose: true,
	});

	if (!isEnabled) {
		return null;
	}

	return (
		<ErrorBoundary attribution={Attribution.AI_MATE}>
			<AISmartButton
				product="confluence"
				cloudId={cloudId}
				orgId={orgId}
				isNav4Enabled
				sidebarApi={sidebarApi}
				productPage={contentType}
				pageMode={pageMode}
				isLiveViewMode={liveViewMode}
				analyticsAttributes={analyticsAttributes}
				isDisabled={isDisabled}
				isSidebarAvailable={isSidebarAvailable}
				topOffset={totalOffset}
				conversationAssistantConfig={conversationAssistantConfig}
				overrideMiniModalWrapper={overrideMiniModalWrapper}
				getActions={getActions}
				prioritizeCustomActionResults={prioritizeCustomActionResults}
				numStartersToShow={numStartersToShow}
				tags={tags}
				uiConfig={uiConfig}
			/>
		</ErrorBoundary>
	);
}
